/**
 * Apoio das telas do acerto em três etapas (informar o retorno → confirmar a conferência →
 * finalizar). Regras de negócio ficam na API e no banco; aqui só se monta o que a tela mostra a
 * partir do acerto que a API devolve (`Settlement`).
 */
import {
  compareMoney,
  fromScaled,
  subtractMoney,
  sumMoney,
  toScaled,
  type EntryMode,
  type Settlement,
} from "@cafegestor/shared";

/** Chave de uma linha de mercadoria: produto + lote (produto sem lote: só o produto). */
export const lineKey = (productId: string, lotId: string | null) => `${productId}|${lotId ?? ""}`;

/** Uma linha do retorno físico: um lote da carga (ou o produto sem lote). */
export type ReturnLine = {
  key: string;
  productId: string;
  lotId: string | null;
  productCode: string;
  productName: string;
  lotCode: string | null;
  lotExpiresOn: string | null;
  unit: string;
  loaded: string;
  sold: string;
  /** esperado de volta (carregado − vendido): só referência */
  expected: string;
  /** declarado pelo vendedor (quando declarou por lote, ou o produto sem lote) */
  declared: string | null;
  /** gravado na API (conferido) */
  counted: string | null;
  countedMode: EntryMode | null;
  countedByEmail: string | null;
  unitCost: string | null;
  /** lote sem movimento na carga (carregamento estornado): não precisa de contagem */
  optional: boolean;
};

export function returnLines(s: Settlement): ReturnLine[] {
  const out: ReturnLine[] = [];
  for (const p of s.products) {
    if (p.productCode === "?") continue;
    for (const l of p.lots) {
      out.push({
        key: lineKey(p.productId, l.lotId),
        productId: p.productId,
        lotId: l.lotId,
        productCode: p.productCode,
        productName: p.productName,
        lotCode: l.lotCode,
        lotExpiresOn: l.lotExpiresOn,
        unit: p.baseUnitCode,
        loaded: l.loaded,
        sold: l.sold,
        expected: l.remaining,
        declared: l.lotId === null ? p.declared : l.declared,
        counted: l.counted,
        countedMode: l.countedMode,
        countedByEmail: l.countedByEmail,
        unitCost: p.unitCost,
        optional: toScaled(l.loaded) === 0n && toScaled(l.sold) === 0n,
      });
    }
  }
  return out;
}

/** Valor digitado na tela e como foi informado (digitado ou pelo atalho "Confere"). */
export type Entry = { value: string; mode: EntryMode };

/** Diferença esperado − contado (positiva = falta; negativa = sobra); null sem contagem. */
export function lineDifference(expected: string, counted: string | null): string | null {
  if (counted === null || counted.trim() === "") return null;
  try {
    return fromScaled(toScaled(expected) - toScaled(counted));
  } catch {
    return null;
  }
}

/** Mesmo número? (as entradas da tela chegam como texto da API: "3", "3.5") */
export function sameQuantity(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  try {
    return toScaled(a) === toScaled(b);
  } catch {
    return false;
  }
}

export type SettlementTotals = {
  /** unidade comum a todos os produtos (null: unidades diferentes, não se soma) */
  unit: string | null;
  loaded: string;
  sold: string;
  expected: string;
  counted: string;
  lines: number;
  countedLines: number;
  products: number;
  lots: number;
};

/** Totais para os cartões de resumo. Só soma quantidades quando a unidade é a mesma. */
export function settlementTotals(s: Settlement): SettlementTotals {
  const lines = returnLines(s);
  const units = new Set(lines.map((l) => l.unit));
  const sum = (f: (l: ReturnLine) => string | null) =>
    fromScaled(lines.reduce((acc, l) => acc + toScaled(f(l) ?? "0"), 0n));
  return {
    unit: units.size === 1 ? [...units][0]! : null,
    loaded: sum((l) => l.loaded),
    sold: sum((l) => l.sold),
    expected: sum((l) => l.expected),
    counted: sum((l) => l.counted),
    lines: lines.filter((l) => !l.optional).length,
    countedLines: lines.filter((l) => !l.optional && l.counted !== null).length,
    products: new Set(lines.map((l) => l.productId)).size,
    lots: lines.filter((l) => l.lotId !== null).length,
  };
}

export type DivergenceLine = {
  label: string;
  quantity: string;
  unit: string;
  value: string | null;
};
export type CashLine = { label: string; amount: string };
export type Divergences = {
  shortage: DivergenceLine[];
  surplus: DivergenceLine[];
  cashShortage: CashLine[];
  cashSurplus: CashLine[];
  /** total em R$ (custo médio atual) das faltas e sobras de mercadoria */
  shortageValue: string;
  surplusValue: string;
  cashShortageTotal: string;
  cashSurplusTotal: string;
  any: boolean;
};

/** As quatro divergências: falta e sobra de mercadoria (por lote), falta e sobra de dinheiro. */
export function divergences(s: Settlement): Divergences {
  const shortage: DivergenceLine[] = [];
  const surplus: DivergenceLine[] = [];
  for (const l of returnLines(s)) {
    const diff = lineDifference(l.expected, l.counted);
    if (diff === null || toScaled(diff) === 0n) continue;
    const q = toScaled(diff);
    const quantity = fromScaled(q < 0n ? -q : q);
    const value =
      l.unitCost === null
        ? null
        : (Math.round(Number(quantity) * Number(l.unitCost) * 100) / 100).toFixed(2);
    const line = {
      label: l.lotCode ? `${l.productCode} · lote ${l.lotCode}` : l.productCode,
      quantity,
      unit: l.unit,
      value,
    };
    (q > 0n ? shortage : surplus).push(line);
  }
  const cashShortage: CashLine[] = [];
  const cashSurplus: CashLine[] = [];
  for (const p of s.payments) {
    if (p.delivered === null) continue;
    const c = compareMoney(p.delivered, p.expected);
    if (c === 0) continue;
    const amount =
      c < 0 ? subtractMoney(p.expected, p.delivered) : subtractMoney(p.delivered, p.expected);
    (c < 0 ? cashShortage : cashSurplus).push({ label: p.paymentMethodName, amount });
  }
  const totalValue = (xs: DivergenceLine[]) => sumMoney(xs.map((x) => x.value ?? "0"));
  return {
    shortage,
    surplus,
    cashShortage,
    cashSurplus,
    shortageValue: totalValue(shortage),
    surplusValue: totalValue(surplus),
    cashShortageTotal: sumMoney(cashShortage.map((c) => c.amount)),
    cashSurplusTotal: sumMoney(cashSurplus.map((c) => c.amount)),
    any:
      shortage.length + surplus.length + cashShortage.length + cashSurplus.length > 0 ||
      s.checkDivergence,
  };
}

/** Linhas que ainda precisam de valor na tela (lote com movimento e forma com recebimento). */
export function pendingEntries(
  s: Settlement,
  counts: Record<string, Entry>,
  money: Record<string, Entry>,
): string[] {
  const out: string[] = [];
  for (const l of returnLines(s)) {
    if (l.optional) continue;
    if ((counts[l.key]?.value ?? "").trim() === "") {
      out.push(l.lotCode ? `${l.productCode} lote ${l.lotCode}` : l.productCode);
    }
  }
  for (const p of s.payments) {
    if (compareMoney(p.expected, "0") === 0 && p.delivered === null) continue;
    if ((money[p.paymentMethodId]?.value ?? "").trim() === "") out.push(p.paymentMethodName);
  }
  return out;
}
